/**
 * Notification-tap router — the web port of Flutter's
 * `PushNavigationService.onPushNotificationAppUpdates`
 * (`lib/services/push_navigation_service.dart:26-360`).
 *
 * One thin dispatcher: it reads `AppNotificationType` off the tapped notification
 * and delegates to the five handler modules under `notificationTap/handlers/`.
 * It holds no behaviour of its own beyond Flutter's case→destination mapping.
 *
 * Why this lives in `presentation/` and not in `domain/services/`. Flutter's
 * `PushNavigationService` is a `services/` class, but ~90% of what it does is
 * `Navigator.*` calls and sheet opening, and `conventions.md` → "Clean
 * Architecture flow" forbids domain code from depending on presentation. It is
 * also not a feature hook (a page feature hook never calls `Navigator.*` and
 * never fires sheets/toasts). It therefore lands as plain presentation modules
 * invoked by `InboxPage`, the layer that legitimately owns navigation + sheets
 * (`presentation.md` → "Page + feature-hook split"). See the story plan's
 * "Design decision — where the dispatcher lives".
 *
 * Synchronous by design — `void`, never `Promise<void>`, and no handler is
 * awaited. Flutter awaits only NINE of its 38 cases (`:80`, `:85`, `:92` and the
 * six roadmap navigations `:245`, `:255`, `:265`, `:274`, `:283`, `:293`),
 * leaving the other 29 fire-and-forget — and those nine await a Dart *route-pop*
 * future that has no web equivalent (`Navigator.*` returns `void`). Keeping the
 * dispatcher synchronous reproduces the un-awaited majority exactly and lets
 * mark-read fire immediately after dispatch, as it does in Flutter for every case
 * that opens a sheet.
 *
 * Mark-read is deliberately NOT here. Flutter's trailing `!read` gate + write
 * (`push_navigation_service.dart:357-359`) belongs to the caller side on web: the
 * gate, the Firestore write and the optimistic flip all live in the inbox feature
 * hook's `markNotificationRead` (Task 7), which `InboxPage` (Task 14) invokes
 * right after this dispatcher returns. That mirrors Flutter's own split, where
 * `_onNotificationItemTap` (`messages_screen.dart:659-677`) owns the read flip and
 * the badge re-check.
 *
 * Use cases in handlers. Mark-read and the current-user refresh are the ONLY two
 * actions the handlers may not run themselves — that state belongs to the inbox
 * feature hook and arrives via `NotificationTapContext.refreshCurrentUser` (or, for
 * mark-read, is invoked by the page after this dispatcher returns). Every other
 * business action a handler triggers goes through a use case called directly from
 * the handler (`retryPaymentNotificationTapHandler`,
 * `scheduledContentNotificationTapHandler`) — the story plan's design decision, and
 * the same shape as `userActionHandlers.ts`. See the `refreshCurrentUser` JSDoc in
 * `notificationTapContext.ts`.
 *
 * Exclusions and deferrals (all task-prompt authorised):
 *  - `liveStarted` — DEFERRED, marker only. Reachable on Flutter solely via a push
 *    notification (the inbox list drops the type — `messages_screen.dart:177-185`,
 *    ported at `useInboxNotifications.ts` / `subscribeToNotificationsUseCase.ts`),
 *    and the web has no push support, so the tap is unreachable. Its permission
 *    gate, `isLive` guard and live-streaming navigation
 *    (`push_navigation_service.dart:160-174`, `:549-583`) are out of scope per
 *    task-prompt line 21, which asks for a marker — see the follow-up marker at
 *    its switch position below (the only such marker in this file).
 *  - `contentAdded` / `earlyAccessContentAdded` — EXCLUDED, no marker. Both types
 *    are filtered out of the list on the page fetch and on every live change in
 *    BOTH clients (`messages_screen.dart:177-185`; `useInboxNotifications.ts`,
 *    `subscribeToNotificationsUseCase.ts`), and Flutter's own row widget has no
 *    live case for them — `notification_item.dart:58` switches per type, its
 *    `contentAdded` case is commented out at `:272` and there is no
 *    `earlyAccessContentAdded` case at all, so both fall through to the `default:`
 *    empty container at `:410-415` (web mirror: `NotificationItemRouter.tsx:10-13`).
 *    Commented-out Flutter code is not a behaviour to port (`conventions.md` →
 *    "Parity is the default"). Their taps are structurally unreachable
 *    (task-prompt line 21) — they are excluded, not deferred, hence no `TODO`.
 *  - The `isPush` branch and the local-notification / Hive side effects —
 *    EXCLUDED per task-prompt line 3: `push_navigation_service.dart:385-399`
 *    (`navigateToContentListScreenWithoutHeroAnimation`) and
 *    `messages_screen.dart:674-676` (`LocalNotificationService.cancelNotification`)
 *    are out of scope for this port; `isPush` is always `false` on web, so
 *    `NotificationTapContext` does not even carry the flag.
 *
 * Coverage: all 38 `AppNotificationType` members (ordinals 0–37) are accounted
 * for — 35 routed to a handler, 1 deferred (`liveStarted`), 2 excluded
 * (`contentAdded`, `earlyAccessContentAdded`). 35 + 1 + 2 = 38, no member is
 * silently absent. The 35 routed labels occupy 33 case bodies, because
 * `premiumPackage` / `identityVerification` / `premiumPackageExpired` share one
 * (Flutter's stacked labels at `:203-206`). Exactly THIRTEEN of the routed
 * members delegate to `handleContentNotificationTap`, matching the count
 * enumerated in that handler's JSDoc: 1 likes-only + 4 comments + 2 gifts +
 * 6 flagless.
 */
import { AppNotificationType } from '@/data/enums/notification/appNotificationType';
import { handleContentNotificationTap } from '@/presentation/feature/inbox/notificationTap/handlers/contentNotificationTapHandler';
import {
  handlePayBoostVideoFailedTap,
  handlePayPremiumVideoFailedTap,
} from '@/presentation/feature/inbox/notificationTap/handlers/retryPaymentNotificationTapHandler';
import { handleRoadmapNotificationTap } from '@/presentation/feature/inbox/notificationTap/handlers/roadmapNotificationTapHandler';
import { handleScheduledContentNotificationTap } from '@/presentation/feature/inbox/notificationTap/handlers/scheduledContentNotificationTapHandler';
import {
  handleCampaignsNotificationTap,
  handleExclusiveSubscriptionRequestedTap,
  handlePremiumPackagesNotificationTap,
  handleReportUserNotificationTap,
  handleSubscribersNotificationTap,
  handleUserDetailsNotificationTap,
} from '@/presentation/feature/inbox/notificationTap/handlers/userNavigationNotificationTapHandler';
import type { NotificationTapContext } from '@/presentation/feature/inbox/notificationTap/notificationTapContext';

/**
 * Dispatches a tapped inbox notification to its destination
 * (`push_navigation_service.dart:27-355`).
 *
 * Called by `InboxPage.handleNotificationItemTap` (Task 14) — the only caller —
 * which then invokes the inbox hook's `markNotificationRead`.
 */
export const dispatchNotificationTap = (context: NotificationTapContext): void => {
  // Flutter: `AppNotificationType.values[notification.info.notificationType]`
  // (`:27-28`). The entity carries the raw ordinal, so the cast is the web
  // equivalent of that index lookup; an unknown future ordinal falls to
  // `default:` below instead of throwing.
  const type = context.notification.info.notificationType as AppNotificationType;

  switch (type) {
    case AppNotificationType.contentLike: // :30-38
      handleContentNotificationTap(context, {
        openContentLikesSheet: true,
        openCommentsSheet: false,
        openGiftsSheet: false,
      });
      break;

    case AppNotificationType.contentComment: // :39-48
      handleContentNotificationTap(context, {
        openContentLikesSheet: false,
        openCommentsSheet: true,
        openGiftsSheet: false,
      });
      break;

    case AppNotificationType.commentLike: // :49-58
      handleContentNotificationTap(context, {
        openContentLikesSheet: false,
        openCommentsSheet: true,
        openGiftsSheet: false,
      });
      break;

    case AppNotificationType.commentReply: // :59-68
      handleContentNotificationTap(context, {
        openContentLikesSheet: false,
        openCommentsSheet: true,
        openGiftsSheet: false,
      });
      break;

    case AppNotificationType.contentGift: // :69-77
      handleContentNotificationTap(context, {
        openContentLikesSheet: false,
        openCommentsSheet: false,
        openGiftsSheet: true,
      });
      break;

    case AppNotificationType.exclusiveSubscription: // :78-82
      handleSubscribersNotificationTap();
      break;

    case AppNotificationType.exclusiveSubscriptionExpired: // :83-89
      handleUserDetailsNotificationTap(context);
      break;

    case AppNotificationType.exclusiveSubscriptionRenewd: // :90-96
      handleUserDetailsNotificationTap(context);
      break;

    case AppNotificationType.exclusiveSubscriptionRequested: // :97-112
      handleExclusiveSubscriptionRequestedTap(context);
      break;

    case AppNotificationType.exclusiveSubscriptionRequestCancelled: // :113-119
      handleUserDetailsNotificationTap(context);
      break;

    case AppNotificationType.exclusiveSubscriptionRequestAccepted: // :120-126
      handleUserDetailsNotificationTap(context);
      break;

    case AppNotificationType.exclusiveSubscriptionRequestDeclined: // :127-133
      handleUserDetailsNotificationTap(context);
      break;

    case AppNotificationType.exclusiveSubscriptionFailedInsufficientCoins: // :134-140
      handleUserDetailsNotificationTap(context);
      break;

    case AppNotificationType.contentMention: // :141-149
      handleContentNotificationTap(context, {
        openContentLikesSheet: false,
        openCommentsSheet: false,
        openGiftsSheet: false,
      });
      break;

    case AppNotificationType.commentMention: // :150-159
      handleContentNotificationTap(context, {
        openContentLikesSheet: false,
        openCommentsSheet: true,
        openGiftsSheet: false,
      });
      break;

    // TODO: @claude add a follow up task for this: liveStarted notification tap (camera/mic permission gate, isLive guard, live-streaming navigation) — Flutter lib/services/push_navigation_service.dart:160-174

    case AppNotificationType.reportContent: // :175-183
      handleContentNotificationTap(context, {
        openContentLikesSheet: false,
        openCommentsSheet: false,
        openGiftsSheet: false,
      });
      break;

    case AppNotificationType.reportUser: // :184-193
      handleReportUserNotificationTap(context);
      break;

    case AppNotificationType.unlockContent: // :194-202
      handleContentNotificationTap(context, {
        openContentLikesSheet: false,
        openCommentsSheet: false,
        openGiftsSheet: true,
      });
      break;

    // Three stacked labels over one shared body, exactly as Flutter (`:203-209`).
    case AppNotificationType.premiumPackage:
    case AppNotificationType.identityVerification:
    case AppNotificationType.premiumPackageExpired:
      handlePremiumPackagesNotificationTap();
      break;

    case AppNotificationType.scheduleContent: // :210-219
      handleScheduledContentNotificationTap(context);
      break;

    case AppNotificationType.scheduleContentPublished: // :220-228
      handleContentNotificationTap(context, {
        openContentLikesSheet: false,
        openCommentsSheet: false,
        openGiftsSheet: false,
      });
      break;

    case AppNotificationType.campaignRanking: // :229-233
      handleCampaignsNotificationTap();
      break;

    // `contentAdded` (`:234-242`) — excluded, structurally unreachable. See the
    // module header; it falls to `default:` as a no-op.

    case AppNotificationType.roadmapLike: // :243-252
      handleRoadmapNotificationTap(context, { openLikes: true, likedType: true });
      break;

    case AppNotificationType.roadmapDislike: // :253-262
      handleRoadmapNotificationTap(context, { openLikes: true, likedType: false });
      break;

    case AppNotificationType.roadmapComment: // :263-271
      handleRoadmapNotificationTap(context, { openComments: true });
      break;

    case AppNotificationType.roadmapCommentLike: // :272-280
      handleRoadmapNotificationTap(context, { openComments: true });
      break;

    case AppNotificationType.roadmapCommentReply: // :281-289
      handleRoadmapNotificationTap(context, { openComments: true });
      break;

    // `:290-300` — Flutter's `openDonations: true` is a verified-dead flag
    // (declared and plumbed, never read), so the web navigates with no flags.
    case AppNotificationType.roadmapDonation:
      handleRoadmapNotificationTap(context, {});
      break;

    case AppNotificationType.payPremiumVideo: // :301-309
      handleContentNotificationTap(context, {
        openContentLikesSheet: false,
        openCommentsSheet: false,
        openGiftsSheet: false,
      });
      break;

    case AppNotificationType.payPremiumVideoFailed: // :310-318
      handlePayPremiumVideoFailedTap(context);
      break;

    // `earlyAccessContentAdded` (`:319-327`) — excluded, structurally
    // unreachable. See the module header; it falls to `default:` as a no-op.

    case AppNotificationType.earlyAccessContentReceived: // :328-336
      handleContentNotificationTap(context, {
        openContentLikesSheet: false,
        openCommentsSheet: false,
        openGiftsSheet: false,
      });
      break;

    case AppNotificationType.payBoostVideo: // :337-345
      handleContentNotificationTap(context, {
        openContentLikesSheet: false,
        openCommentsSheet: false,
        openGiftsSheet: false,
      });
      break;

    case AppNotificationType.payBoostVideoFailed: // :346-354
      handlePayBoostVideoFailedTap(context);
      break;

    // Flutter's switch is exhaustive over the enum and has no `default`. The web
    // adds one so a deferred (`liveStarted`), excluded (`contentAdded`,
    // `earlyAccessContentAdded`) or unknown future ordinal is a silent no-op
    // rather than a crash.
    default:
      break;
  }
};
